import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { companyApi, integrationApi } from '../api';
import { createErrorToast, createSuccessToast, createToast } from '../redux/modules/app';
import { hideModal } from '../redux/modules/modal';
import { removeArrayItemAt } from '../utils/arrayUtil';
import { queryClient } from '../utils/queryClientUtil';

import companyKeys from './models/company/keys';
import meKeys from './models/me/keys';
import { camelCaseApiResponse, getPageData } from './util';

/* QUERIES
========================================================= */
export const useCompanyData = (companyId, params = {}, options = {}) => useQuery(
  companyKeys.company(companyId),
  () => camelCaseApiResponse(() => companyApi.getCompanyById(
    companyId,
    { include: params.include }
  )),
  options
);

export const useCompanyUsers = (companyId, options = {}) => useQuery(
  companyKeys.companyUsers(companyId),
  () => camelCaseApiResponse(() => companyApi.getCompanyUsers(companyId)),
  options
);

export const useCompanyActiveUsers = (companyId, options = {}) => useQuery(
  companyKeys.companyActiveUsers(companyId),
  async () => {
    const users = await camelCaseApiResponse(() => companyApi.getCompanyUsers(companyId));

    return users.filter(user => user.isActive);
  },
  options
);

export const useAllCompanyPolicyTypesData = (companyId, options = {}) => useQuery(
  companyKeys.companyAllPolicyTypes(companyId),
  () => camelCaseApiResponse(() => companyApi.getCompanyPolicyTypes(companyId, { deleted: true })),
  options
);

export const useCompanyDocumentTypes = (companyId, options = {}) => useQuery(
  companyKeys.companyAllDocumentTypes(companyId),
  () => camelCaseApiResponse(() => companyApi.getCompanyDocumentTypes(companyId, { deleted: true })),
  options
);

export const useCompanyItemTypesData = (companyId, options = {}) => useQuery(
  companyKeys.companyItemTypes(companyId),
  () => camelCaseApiResponse(() => companyApi.getCompanyItemTypes(companyId, { include: 'item_type_properties' })),
  options
);

export const useCompanyCodes = (companyId, options = {}) => useQuery(
  companyKeys.companyCodes(companyId),
  () => camelCaseApiResponse(() => companyApi.getCompanyCodes(companyId)),
  options
);

export const useCompanyCustomRoles = (companyId, params = {}, options = {}) => useQuery(
  companyKeys.companyCustomRoles(companyId, params),
  () => camelCaseApiResponse(() => companyApi.getCompanyCustomRoles(companyId, { include: params?.include })),
  {
    ...options,
    enabled: !!companyId
  }
);

export const useCompanyPermissions = (companyId, options = {}) => useQuery(
  companyKeys.companyPermissions(companyId),
  () => camelCaseApiResponse(() => companyApi.getCompanyPermissions(companyId)),
  options
);

export const useCompanyIdFromProcoreCompanyId = (procoreCompanyInd, options = {}) => useQuery(
  companyKeys.companyIdFromProcoreCompanyId(procoreCompanyInd),
  () => camelCaseApiResponse(() => integrationApi.getCompanyIdFromProcoreCompanyId(procoreCompanyInd)),
  options
);

export const useCompanyRequirementTemplateFolders = ({
  companyId,
  pageSize = 25,
  options = {}
}) => useInfiniteQuery(
  companyKeys.companyRequirementTemplateFolders(companyId),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => companyApi.listRequirementTemplateFolders(
    companyId,
    { page: pageParam, pageSize }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useCompanyInsureds = ({
  companyId,
  searchTerm = null,
  pageSize = 25,
  options = {}
}) => useInfiniteQuery(
  companyKeys.companyInsuredsList(companyId, { searchTerm }),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => companyApi.listCompanyInsureds(
    companyId,
    { page: pageParam, pageSize },
    { ...(searchTerm && { insured_company_name: searchTerm }) }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useCompanyQuestionCategories = ({
  companyId,
  searchTerm = null,
  withDeleted = true,
  requirementTemplateId = null,
  pageSize = 25,
  options = {}
}) => useInfiniteQuery(
  companyKeys.companyQuestionCategories(companyId, { searchTerm, requirementTemplateId }),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => companyApi.listCompanyQuestions(
    companyId,
    { page: pageParam, pageSize },
    {
      ...({ with_deleted: !!withDeleted }),
      ...(searchTerm && { display_name: searchTerm }),
      ...(requirementTemplateId && { requirement_template_id: requirementTemplateId })
    }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

/* MUTATIONS
========================================================= */

export const useCreateCompanyRole = ({
  companyId,
  options = {}
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const companyRolesListKeys = companyKeys.companyCustomRoles(companyId, { include: 'permissions' });

  return useMutation(
    ({ name, description, permissions }) => camelCaseApiResponse(() => companyApi.createCompanyCustomRole(companyId, name, permissions, description)),
    {
      onMutate: async data => {
        dispatch(createSuccessToast({ title: `${data.name} Role Created` }));

        await queryClient.cancelQueries(companyRolesListKeys);

        const previousCompanyRoles = queryClient.getQueryData(companyRolesListKeys);

        const uuid = uuidv4();

        queryClient.setQueryData(companyRolesListKeys, [...previousCompanyRoles, {
          id: uuid,
          name: data.name,
          displayName: data.name,
          description: data.description,
          companyId,
          createdAt: null,
          updatedAt: null,
          permissions: data.permissions
        }]);

        return { previousCompanyRoles };
      },
      onError: async (error, _variables, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your role was not created successfully.',
          error
        }));

        queryClient.setQueryData(companyRolesListKeys, context.previousCompanyRoles);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(companyRolesListKeys);
      },
      ...options
    }
  );
};

export const useDeleteCompanyRole = ({
  companyId,
  options = {}
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const companyRolesListKeys = companyKeys.companyCustomRoles(companyId, { include: 'permissions' });

  return useMutation(
    ({ roleId }) => camelCaseApiResponse(() => companyApi.deleteCompanyRole(companyId, roleId)),
    {
      onMutate: async data => {
        await queryClient.cancelQueries(companyRolesListKeys);

        const previousCompanyRoles = queryClient.getQueryData(companyRolesListKeys);

        const updatedRoles = removeArrayItemAt(previousCompanyRoles, 'id', data.roleId);

        queryClient.setQueryData(companyRolesListKeys, updatedRoles);

        dispatch(createSuccessToast({ title: 'Role Deleted' }));
      },
      onError: async (error, _variables, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your role was not deleted successfully.',
          error
        }));

        queryClient.setQueryData(companyRolesListKeys, context.previousCompanyRoles);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(companyRolesListKeys);
      },
      ...options
    }
  );
};

export const useUpdateCompanyRole = ({
  companyId,
  options = {}
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const companyRolesListKeys = companyKeys.companyCustomRoles(companyId, { include: 'permissions' });

  return useMutation(
    ({ roleId, name, description, permissions }) => camelCaseApiResponse(() => companyApi.updateCompanyRole(companyId, roleId, name, permissions, description)),
    {
      onMutate: async data => {
        dispatch(createSuccessToast({ title: `${data.name} Role Updated` }));

        await queryClient.cancelQueries(companyRolesListKeys);

        const previousCompanyRoles = queryClient.getQueryData(companyRolesListKeys);

        const updatedRoles = previousCompanyRoles.map(role => {
          if (role.id !== data.roleId) return role;

          return {
            ...role,
            name: data.name,
            displayName: data.name,
            description: data.description,
            permissions: data.permissions
          };
        });

        queryClient.setQueryData(companyRolesListKeys, updatedRoles);

        return { previousCompanyRoles };
      },
      onError: async (error, _variables, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your role was not updated successfully.',
          error
        }));

        queryClient.setQueryData(companyRolesListKeys, context.previousCompanyRoles);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(companyRolesListKeys);
      },
      ...options
    }
  );
};

export const useRequestCompanyFeature = ({
  companyId,
  options = {}
}) => {
  const dispatch = useDispatch();

  return useMutation(
    ({ featureTypeId }) => camelCaseApiResponse(() => companyApi.requestFeatureType(companyId, featureTypeId)),
    {
      onError: async (error, _variables, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your request was not sent successfully.',
          error
        }));
      },
      ...options
    }
  );
};

export const useCreateQuestionCategory = ({
  companyId,
  options = {}
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const companyQuestionCategoriesKeys = companyKeys.companyQuestionCategoryBase(companyId);

  return useMutation(
    ({ name }) => camelCaseApiResponse(() => companyApi.createCompanyQuestionCategory(companyId, name)),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(companyQuestionCategoriesKeys);
      },
      onError: async (error, _variables, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your question category was not created successfully.',
          error
        }));
      },
      ...options
    }
  );
};

export const useCreateQuestion = ({
  companyId,
  options = {}
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const companyQuestionCategoriesKeys = companyKeys.companyQuestionCategoryBase(companyId);

  return useMutation(
    ({
      questionCategoryId,
      name,
      question,
      redFlag
    }) => camelCaseApiResponse(() => companyApi.createCompanyQuestion(companyId, questionCategoryId, name, question, redFlag)),
    {
      onMutate: () => {
        dispatch(createToast({ title: 'Creating new question...' }));
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(companyQuestionCategoriesKeys);
        dispatch(createSuccessToast({ title: 'Question created successfully' }));
      },
      onError: async (error, _variables, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your question was not created successfully.',
          error
        }));
      },
      ...options
    }
  );
};

export const useUpdateCompany = ({
  companyId,
  options
}) => {
  const dispatch = useDispatch();

  return useMutation(
    ({
      company_name,
      short_name,
      company_address_1,
      company_address_2,
      company_city,
      company_state,
      company_zip,
      company_phone,
      company_fax,
      company_contact,
      company_alias,
      record_tier
    }) => camelCaseApiResponse(() => companyApi.updateCompany(companyId, {
      company_name,
      short_name,
      company_address_1,
      company_address_2,
      company_city,
      company_state,
      company_zip,
      company_phone,
      company_fax,
      company_contact,
      company_alias,
      record_tier
    })),
    {
      onMutate: () => {
        dispatch(createToast({ title: 'Updating Company...' }));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(meKeys.companies);
        dispatch(createSuccessToast({ title: 'Company updated successfully' }));
        dispatch(hideModal());
      },
      onError: () => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your company was not updated successfully.'
        }));
      },
      ...options
    }
  );
};
